// Voice integration screen-pop: turns the backend's realtime `voice.call`
// event into a toast that opens the matched patient, or Call Logs filtered to
// the caller's number when nobody matched (where "Create lead from call" and
// matching already live). Pure functions plus one dedupe set; the PieSocket
// wiring is in LiveNotificationHandler.
//
// Payload (Voice Integration Spec, "Realtime"):
//   { type: 'voice.call', call_id, event, from, site_number, lead: {id, name} | null, user }
// where event is 'call.ringing' | 'call.answered'. Only inbound ringing pops;
// 'call.answered' is accepted (no toast) so the backend can send both.
import { PATH_DASHBOARD } from '../../routes/paths';

export const VOICE_CALL_EVENT = 'voice-call';
export const VOICE_CALL_TYPE = 'voice.call';
export const VOICE_CALL_VERB = 'voice_call_ringing';
// window CustomEvent re-broadcast by LiveNotificationHandler for every voice.call
// payload (ringing, answered, ended), so a mounted Call Logs page can refetch.
export const VOICE_CALL_WINDOW_EVENT = 'voice-call-event';

// call_ids already shown this session; a reconnect replay or a duplicate
// publish must not pop the same call twice.
export const shownVoiceCallIds = new Set();
const SHOWN_TTL_MS = 10 * 60 * 1000;

export const parseVoiceCallPayload = (data) => {
    if (!data) return null;
    let payload = data;
    if (typeof data === 'string') {
        try { payload = JSON.parse(data); } catch { return null; }
    }
    if (!payload || typeof payload !== 'object') return null;
    // Some publishers wrap the event: { data: {...} } or { type, data: {...} }.
    if (payload.data && typeof payload.data === 'object' && !payload.call_id && payload.data.call_id) payload = { ...payload, ...payload.data };
    return payload.type === VOICE_CALL_TYPE || payload.call_id ? payload : null;
};

// A superuser's rooms follow the practice they have open, so a call for any other
// practice is never shown, even if a late message arrives after a switch (CU-869fk1hpj).
// Older backends send no practice_id; those rely on the per-practice room alone.
export const isForAnotherPractice = (data, practiceId) => {
    const payload = parseVoiceCallPayload(data);
    return Boolean(payload && payload.practice_id != null && String(payload.practice_id) !== String(practiceId));
};

// Explicit only: a payload without `direction` never pops (an outbound call
// popped as "Incoming call" on staging when the publisher omitted it).
const isInbound = (p) => p.direction === 'inbound';
const callerLabel = (p) => (p.caller_id_withheld || !p.from ? 'Withheld number' : p.from);
const userLabel = (u) => (u && typeof u === 'object' ? (u.name || u.extension || '') : '');

// Short human date for the patient summary line ("Tue 21 Oct, 09:30"), in the
// practice's zone (UK practices; the payload carries no zone) rather than the
// machine's, so a receptionist on a laptop set to UTC still reads clinic time.
const whenLabel = (value, timeZone = 'Europe/London') => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
};

// The "who is calling" line the receptionist reads before answering. When the
// backend attaches `lead_summary` (status, next/last appointment, open tasks,
// outstanding treatment, assignee) it is folded into a second sentence, so the
// pop answers "who, and what's going on with them" at a glance.
export const voiceCallSummary = (p) => {
    const s = p.lead_summary;
    if (!s || typeof s !== 'object') return '';
    const bits = [];
    if (s.status_label || s.status) bits.push(String(s.status_label || s.status).replace(/_/g, ' ').toLowerCase());
    if (s.next_appointment_at) bits.push(`next appointment ${whenLabel(s.next_appointment_at)}`);
    else if (s.last_appointment_at) bits.push(`last seen ${whenLabel(s.last_appointment_at)}`);
    if (s.outstanding_treatment) bits.push(`outstanding: ${s.outstanding_treatment}`);
    if (Number(s.open_tasks) > 0) bits.push(`${s.open_tasks} open task${Number(s.open_tasks) === 1 ? '' : 's'}`);
    if (s.assigned_user_name) bits.push(`with ${s.assigned_user_name}`);
    return bits.join(' · ');
};

export const voiceCallDescription = (p) => {
    const who = p.lead?.name ? `${p.lead.name} (${callerLabel(p)})` : callerLabel(p);
    const parts = [`Incoming call from ${who}`];
    if (p.site_number) parts.push(`on ${p.site_number}`);
    const ringing = userLabel(p.user);
    if (ringing) parts.push(`· ringing ${ringing}`);
    const summary = voiceCallSummary(p);
    return summary ? `${parts.join(' ')}. ${summary.charAt(0).toUpperCase()}${summary.slice(1)}` : parts.join(' ');
};

// Build the toast, or null when this payload should not pop.
export const buildVoiceCallToast = (payload, practiceId) => {
    const p = parseVoiceCallPayload(payload);
    if (practiceId == null || practiceId === '' || !p || p.event !== 'call.ringing' || !isInbound(p) || p.is_test) return null;
    const leadId = p.lead?.id;
    const actionLink = leadId
        ? PATH_DASHBOARD.general.leadDetail.replace(':practiceId', practiceId).replace(':id', leadId).replace('/:tab?', '')
        : PATH_DASHBOARD.callLogs.practice(practiceId, { query: p.from || '' });
    return {
        id: `voice_call_${p.call_id || Date.now()}`,
        verb: VOICE_CALL_VERB,
        description: voiceCallDescription(p),
        priority: 'urgent',
        requires_action: true,
        action_label: leadId ? 'Open patient' : 'Find or create patient',
        action_link: actionLink,
        data: { voiceCall: p, leadId: leadId || null },
    };
};

// Dedupe wrapper used by the live handler.
export const voiceCallToastFor = (payload, practiceId) => {
    const toast = buildVoiceCallToast(payload, practiceId);
    if (!toast) return null;
    const key = String(toast.data.voiceCall.call_id || toast.id);
    if (shownVoiceCallIds.has(key)) return null;
    shownVoiceCallIds.add(key);
    setTimeout(() => shownVoiceCallIds.delete(key), SHOWN_TTL_MS);
    return toast;
};
